import { useState } from 'react';
import { createCheckoutSession, FREE_STANDARD_PLAN, PRO_PLANS, STRIPE_CONFIGURED } from '../lib/stripe';
import { useAuth } from '../context/AuthContext';
import { redirectToSignedOutSignIn } from '../lib/navigation';
import { BILLING_UNAVAILABLE_MESSAGE, SUBSCRIPTION_LOOKUP_FAILED_MESSAGE } from '../lib/userMessages';

const ALL_PLANS = PRO_PLANS.map(plan => ({
  ...plan,
  label: `Pro — ${plan.label}`,
  description: 'Supply chain intelligence, builder contacts, and more',
  isPro: true,
}));

const infoMessageStyle = {
  marginBottom: '14px',
  padding: '10px 14px',
  background: 'rgba(212,168,74,0.1)',
  border: '1px solid rgba(212,168,74,0.3)',
  borderRadius: '8px',
  fontSize: '13px',
  color: '#d4a84a',
  textAlign: 'center',
  lineHeight: 1.45,
};

export default function PlanSelector({ subscriptionStatus, subscriptionError, allowFreeStandard = false, onContinueStandard }) {
  const [loadingPriceId, setLoadingPriceId] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const { signOut } = useAuth();

  const isReturning = subscriptionStatus === 'canceled' || subscriptionStatus === 'past_due';
  const isAccountChoice = allowFreeStandard && typeof onContinueStandard === 'function';

  const handleContinueStandard = () => {
    if (loadingPriceId || !onContinueStandard) return;
    onContinueStandard();
  };

  const handleSelectPlan = async (plan) => {
    if (loadingPriceId || !plan) return;
    setCheckoutError(null);
    setLoadingPriceId(plan.key);
    try {
      await createCheckoutSession(plan);
    } catch (err) {
      console.error('[SiteScope] PlanSelector checkout error:', err.message);
      setCheckoutError(BILLING_UNAVAILABLE_MESSAGE);
      setLoadingPriceId(null);
    }
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      redirectToSignedOutSignIn();
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: '#0a0f1a',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
      overflowY: 'auto',
    }}>
      <div style={{ maxWidth: '440px', width: '100%', paddingTop: '16px', paddingBottom: '32px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{
            width: '56px', height: '56px', borderRadius: '14px',
            background: 'linear-gradient(135deg,#d4a84a,#d4782f)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '28px', fontWeight: '700', color: '#0a0f1a',
            fontFamily: 'Georgia, serif',
            border: '2px solid rgba(212,168,74,0.3)',
          }}>S</div>
        </div>

        {/* Heading */}
        {isAccountChoice ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '6px', fontFamily: 'Georgia, serif', fontSize: '22px', color: '#e8e0d8', fontWeight: '700' }}>
              Choose your account
            </div>
            <div style={{ textAlign: 'center', marginBottom: '28px', fontSize: '14px', color: '#9ca3af', lineHeight: 1.6 }}>
              Start free with Standard, or choose Pro now for supply-chain intelligence.
            </div>
          </>
        ) : isReturning ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '6px', fontFamily: 'Georgia, serif', fontSize: '22px', color: '#e8e0d8', fontWeight: '700' }}>
              Your subscription has ended.
            </div>
            <div style={{ textAlign: 'center', marginBottom: '28px', fontSize: '14px', color: '#9ca3af', lineHeight: 1.6 }}>
              Choose a Pro plan to restore supply-chain intelligence. Standard access is free.
            </div>
          </>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '6px', fontFamily: 'Georgia, serif', fontSize: '22px', color: '#e8e0d8', fontWeight: '700' }}>
              Welcome to SiteScope
            </div>
            <div style={{ textAlign: 'center', marginBottom: '28px', fontSize: '14px', color: '#9ca3af', lineHeight: 1.6 }}>
              Standard access is free. Choose a Pro plan when you need supply-chain intelligence.
            </div>
          </>
        )}

        {/* Plan cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
          {isAccountChoice && FREE_STANDARD_PLAN && (
            <>
              <button
                onClick={handleContinueStandard}
                disabled={!!loadingPriceId}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '16px 18px', borderRadius: '10px',
                  border: '1.5px solid #1e2538',
                  background: '#0c1220',
                  color: loadingPriceId ? '#4b5563' : '#e8e0d8',
                  cursor: loadingPriceId ? 'not-allowed' : 'pointer',
                  opacity: loadingPriceId ? 0.6 : 1,
                  textAlign: 'left',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                onMouseEnter={e => {
                  if (!loadingPriceId) {
                    e.currentTarget.style.borderColor = '#6b7280';
                    e.currentTarget.style.background = '#101827';
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#1e2538';
                  e.currentTarget.style.background = '#0c1220';
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '3px', fontFamily: "'DM Sans',sans-serif" }}>
                    Standard
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>
                    Free DA browsing with 1 tracked DA and 1 watch zone
                  </div>
                </div>
                <div style={{ marginLeft: '16px', flexShrink: 0, fontSize: '15px', fontWeight: '700', color: '#aeb8c7', fontFamily: "'DM Sans',sans-serif" }}>
                  Free
                </div>
              </button>

              {STRIPE_CONFIGURED && <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0', marginTop: '2px' }}>
                <div style={{ flex: 1, height: '1px', background: '#1e2538' }} />
                <span style={{ fontSize: '11px', color: '#4b5563', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '600' }}>Paid Pro Plans</span>
                <div style={{ flex: 1, height: '1px', background: '#1e2538' }} />
              </div>}
            </>
          )}
          {!STRIPE_CONFIGURED && (
            <div style={infoMessageStyle}>{BILLING_UNAVAILABLE_MESSAGE}</div>
          )}
          {ALL_PLANS.map((plan, i) => {
            const isPlanLoading = loadingPriceId === plan.key;
            const isDisabled = !!loadingPriceId;

            const showDivider = !isAccountChoice && i === 0;

            return (
              <div key={plan.key}>
                {showDivider && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', marginTop: '2px' }}>
                    <div style={{ flex: 1, height: '1px', background: '#1e2538' }} />
                    <span style={{ fontSize: '11px', color: '#4b5563', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '600' }}>Paid Pro Plans</span>
                    <div style={{ flex: 1, height: '1px', background: '#1e2538' }} />
                  </div>
                )}
                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={isDisabled}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 18px', borderRadius: '10px',
                    border: plan.isPro ? '1.5px solid #d4a84a55' : '1.5px solid #1e2538',
                    background: isPlanLoading ? 'rgba(212,168,74,0.1)' : plan.isPro ? '#0f1629' : '#0c1220',
                    color: isDisabled && !isPlanLoading ? '#4b5563' : '#e8e0d8',
                    cursor: isDisabled ? (isPlanLoading ? 'wait' : 'not-allowed') : 'pointer',
                    opacity: isDisabled && !isPlanLoading ? 0.6 : 1,
                    textAlign: 'left',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={e => {
                    if (!isDisabled) {
                      e.currentTarget.style.borderColor = '#d4a84a';
                      e.currentTarget.style.background = 'rgba(212,168,74,0.08)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isPlanLoading) {
                      e.currentTarget.style.borderColor = plan.isPro ? '#d4a84a55' : '#1e2538';
                      e.currentTarget.style.background = plan.isPro ? '#0f1629' : '#0c1220';
                    }
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '3px', fontFamily: "'DM Sans',sans-serif" }}>
                      {plan.label}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.4 }}>
                      {plan.description}
                    </div>
                  </div>
                  <div style={{ marginLeft: '16px', flexShrink: 0, fontSize: '15px', fontWeight: '700', color: '#d4a84a', fontFamily: "'DM Sans',sans-serif" }}>
                    {isPlanLoading ? 'Loading…' : plan.price}
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {/* Error */}
        {subscriptionError && (
          <div style={infoMessageStyle}>
            {SUBSCRIPTION_LOOKUP_FAILED_MESSAGE}
          </div>
        )}

        {checkoutError && (
          <div style={infoMessageStyle}>
            {checkoutError}
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#4b5563', marginBottom: '20px' }}>
          Standard is free with signup. Pro prices include GST and are billed monthly in AUD. Cancel anytime.
        </div>

        {/* Sign out */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            style={{
              background: 'none', border: 'none', cursor: signingOut ? 'wait' : 'pointer',
              color: '#4b5563', fontSize: '13px', fontFamily: "'DM Sans',sans-serif",
              textDecoration: 'underline', padding: '4px 8px',
            }}
          >
            {signingOut ? 'Signing out…' : 'Not ready? Sign out'}
          </button>
        </div>

      </div>
    </div>
  );
}
